// S32 Unlock (Tool Plan §4.15; D-80, D-69, D-77; IR-05, IR-21, IR-22; I-1a Batch 4).
//
// - Title = the neutral product name; no counts, last-entry time or previews.
// - One field with "show". "Opening…" is drawn before Argon2id runs (main thread,
//   IP2-29 (4)). After wrong tries a growing delay, said calmly; no blame words;
//   NO auto-wipe.
// - "I can't open it": the honest line; the recovery code (if one was made) —
//   then a new lock is chosen at once; or delete everything, which needs its own
//   screen, a tick and a button (never one tap).
// - Help now + Hide sit in the top bar (Batch 5; Help now works before unlock).
//   If Hide or a lock removes this screen while it holds the store opened with
//   the recovery code (choosing the new lock), that store is closed at once.

import { type FormEvent, useEffect, useRef, useState } from 'react'

import type { EncryptedStore, LockSession, UnlockResult } from '../lock'
import { nextFrame } from '../ui/nextFrame'
import { ScreenTitle } from '../ui/ScreenTitle'
import { SecretField } from '../ui/SecretField'

import { common, PRODUCT_NAME, setLock, unlock } from './copy'
import { SetLock } from './SetLock'

type Mode = 'enter' | 'cant-open' | 'recovery' | 'new-lock' | 'confirm-delete'
type Via = 'app-lock' | 'recovery'

interface UnlockProps {
  readonly session: LockSession
  /** A try starts (the shell's state machine: locked → unlocking). */
  readonly onTry: () => void
  /** The try did not open the diary (unlocking → locked). */
  readonly onFailed: () => void
  readonly onOpened: (store: EncryptedStore, failedTries: number) => void
  readonly onDeleted: () => void
}

interface Recovered {
  readonly store: EncryptedStore
  readonly failedTries: number
  readonly code: string
}

export function Unlock({ session, onTry, onFailed, onOpened, onDeleted }: UnlockProps): React.JSX.Element {
  const [mode, setMode] = useState<Mode>('enter')
  const [value, setValue] = useState('')
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const [wait, setWait] = useState(0)
  const [hasRecovery, setHasRecovery] = useState(false)
  const [understood, setUnderstood] = useState(false)
  // Opened with the recovery code; held only until the new lock is saved.
  const recovered = useRef<Recovered | null>(null)

  useEffect(() => {
    session.preload()
    let live = true
    session
      .status()
      .then((status) => {
        if (live) {
          setWait(status.waitSeconds)
          setHasRecovery(status.hasRecoveryCode)
        }
      })
      .catch(() => undefined)
    return () => {
      live = false
    }
  }, [session])

  // Never leave an open store behind when this screen goes away (Hide, lock).
  useEffect(
    () => () => {
      recovered.current?.store.close()
      recovered.current = null
    },
    [],
  )

  // Calm countdown. The session's own clock decides; this only shows it.
  useEffect(() => {
    if (wait <= 0) {
      return undefined
    }
    const timer = setTimeout(() => setWait((w) => Math.max(0, w - 1)), 1000)
    return () => clearTimeout(timer)
  }, [wait])

  const go = (next: Mode): void => {
    setMode(next)
    setMessage(null)
    setValue('')
    setUnderstood(false)
  }

  const tryOpen = async (via: Via): Promise<void> => {
    if (busy || wait > 0 || value.length === 0) {
      return
    }
    const typed = value
    setBusy(true)
    setMessage(null)
    onTry()
    await nextFrame()
    let result: UnlockResult | null
    try {
      result = await session.unlock(typed, via)
    } catch {
      result = null
    }
    setValue('')

    if (result?.kind === 'opened') {
      if (via === 'recovery') {
        recovered.current = { store: result.store, failedTries: result.failedTries, code: typed }
        setBusy(false)
        setMode('new-lock')
        return
      }
      onOpened(result.store, result.failedTries)
      return
    }

    setBusy(false)
    onFailed()
    switch (result?.kind) {
      case 'wrong':
        setMessage(unlock.wrong)
        setWait(result.waitSeconds)
        break
      case 'wait':
        setWait(result.waitSeconds)
        break
      case 'not-a-code':
        setMessage(via === 'recovery' ? unlock.notACode : null)
        break
      case 'cannot-open':
        setMessage(unlock.damaged)
        break
      default:
        setMessage(common.tryAgainLater)
    }
  }

  const saveNewLock = async (secret: string): Promise<void> => {
    const held = recovered.current
    if (!held) {
      return
    }
    setBusy(true)
    setMessage(null)
    await nextFrame()
    try {
      await session.replaceAppLock(held.code, secret)
      recovered.current = null
      onOpened(held.store, held.failedTries)
    } catch {
      setBusy(false)
      setMessage(common.tryAgainLater)
    }
  }

  const deleteEverything = async (): Promise<void> => {
    setBusy(true)
    setMessage(null)
    try {
      await session.deleteEverything(() => setMessage(unlock.closeOthers))
      onDeleted()
    } catch {
      setBusy(false)
      setMessage(common.tryAgainLater)
    }
  }

  const submit =
    (via: Via) =>
    (event: FormEvent): void => {
      event.preventDefault()
      void tryOpen(via)
    }

  const problem = message ? (
    <p className="screen__problem" role="alert">
      {message}
    </p>
  ) : null
  const waitLine = wait > 0 ? <p className="screen__status">{unlock.wait(wait)}</p> : null
  const opening = busy ? (
    <p className="screen__status" role="status">
      {common.opening}
    </p>
  ) : null

  let body: React.JSX.Element
  switch (mode) {
    case 'enter':
      body = (
        <form className="screen__body" onSubmit={submit('app-lock')} noValidate>
          <ScreenTitle>{PRODUCT_NAME}</ScreenTitle>
          <SecretField id="unlock-secret" label={unlock.field} value={value} onChange={setValue} disabled={busy} />
          {problem}
          {waitLine}
          {opening}
          <div className="actions">
            <button type="submit" className="button button--primary" disabled={busy || wait > 0 || value.length === 0}>
              {unlock.open}
            </button>
            <button type="button" className="button button--quiet" disabled={busy} onClick={() => go('cant-open')}>
              {unlock.cantOpen}
            </button>
          </div>
        </form>
      )
      break
    case 'cant-open':
      body = (
        <div className="screen__body">
          <ScreenTitle>{unlock.cantOpenTitle}</ScreenTitle>
          <p className="screen__text">{unlock.cantOpenText}</p>
          <div className="actions">
            {hasRecovery ? (
              <button type="button" className="button button--primary" onClick={() => go('recovery')}>
                {unlock.useRecovery}
              </button>
            ) : null}
            <button type="button" className="button button--quiet" onClick={() => go('confirm-delete')}>
              {unlock.deleteAll}
            </button>
            <button type="button" className="button button--quiet" onClick={() => go('enter')}>
              {common.back}
            </button>
          </div>
        </div>
      )
      break
    case 'recovery':
      body = (
        <form className="screen__body" onSubmit={submit('recovery')} noValidate>
          <ScreenTitle>{unlock.useRecovery}</ScreenTitle>
          <SecretField id="recovery-code" label={unlock.recoveryField} value={value} onChange={setValue} disabled={busy} />
          {problem}
          {waitLine}
          {opening}
          <div className="actions">
            <button type="submit" className="button button--primary" disabled={busy || wait > 0 || value.length === 0}>
              {unlock.open}
            </button>
            <button type="button" className="button button--quiet" disabled={busy} onClick={() => go('cant-open')}>
              {common.back}
            </button>
          </div>
        </form>
      )
      break
    case 'new-lock':
      body = (
        <SetLock
          title={setLock.newLockTitle}
          intro={setLock.newLockText}
          submitLabel={common.continue}
          busy={busy}
          message={message}
          onChosen={(secret) => void saveNewLock(secret)}
        />
      )
      break
    case 'confirm-delete':
      body = (
        <div className="screen__body">
          <ScreenTitle>{unlock.confirmTitle}</ScreenTitle>
          <p className="screen__text">{unlock.confirmText}</p>
          <label className="check">
            <input type="checkbox" checked={understood} disabled={busy} onChange={(e) => setUnderstood(e.target.checked)} />
            <span>{unlock.confirmCheck}</span>
          </label>
          {problem}
          <div className="actions">
            <button type="button" className="button button--primary" disabled={busy} onClick={() => go('cant-open')}>
              {unlock.keep}
            </button>
            <button
              type="button"
              className="button button--danger"
              disabled={busy || !understood}
              onClick={() => void deleteEverything()}
            >
              {unlock.confirmDelete}
            </button>
          </div>
        </div>
      )
      break
  }

  return (
    <section key={mode} className="screen">
      {body}
    </section>
  )
}
